import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '@services/api';
import { useTranslationStore } from '@stores/translationStore';
import { useToast } from '@stores/toastStore';
import { useSignalR } from '@hooks/useSignalR';
import { playSound } from '../services/soundService';
import PageSpinner from '@components/PageSpinner';
import PageHeader from '@components/PageHeader';
import { emitActivityChanged } from '@utils/activityEvents';
import './MaintenancePage.scss';

// Get hotel slug from URL params or localStorage fallback
const getHotelSlug = (paramSlug: string | undefined): string | null => {
  if (paramSlug && paramSlug !== 'undefined') return paramSlug;
  return localStorage.getItem('gcxp_hotel_slug');
};

interface MaintenanceIssueType {
  id: number;
  name: string;
  description?: string;
  icon?: string;
}

interface MaintenanceCategory {
  id: number;
  name: string;
  icon: string;
  displayOrder: number;
  issueTypes: MaintenanceIssueType[];
}

interface ConfigResponse {
  isEnabled: boolean;
  guestCanReport: boolean;
  welcomeMessage?: string;
  guestName: string;
  roomNumber: string;
  categories: MaintenanceCategory[];
}

interface MaintenanceReport {
  id: string;
  subject: string;
  description?: string;
  status: string;
  statusCode: number;
  priority: string;
  priorityCode: number;
  categoryName?: string;
  categoryIcon?: string;
  issueTypeName?: string;
  reportedAt: string;
  resolvedAt?: string;
  resolutionNotes?: string;
  progress: { step: number; label: string; isComplete: boolean; isCurrent: boolean }[];
}

type ViewState = 'selection' | 'form' | 'submitting' | 'tracking' | 'list';

export default function MaintenancePage() {
  const { hotelSlug: paramSlug } = useParams();
  const hotelSlug = getHotelSlug(paramSlug);
  const { loadTranslations, t, language } = useTranslationStore();
  const { toast } = useToast();

  const [view, setView] = useState<ViewState>('selection');
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [selectedCategory, setSelectedCategory] = useState<MaintenanceCategory | null>(null);
  const [selectedIssueType, setSelectedIssueType] = useState<MaintenanceIssueType | null>(null);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [locationDescription, setLocationDescription] = useState('');
  const [priority, setPriority] = useState<number>(2); // Normal
  
  // Tracking state
  const [currentReport, setCurrentReport] = useState<MaintenanceReport | null>(null);
  const [myReports, setMyReports] = useState<MaintenanceReport[]>([]);
  const { on } = useSignalR();
  const currentReportIdRef = useRef<string | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Listen for real-time report status updates via SignalR
  useEffect(() => {
    const unsub = on('ReportStatusChanged', (data) => {
      const { reportId, status } = data as { reportId: string; status: string };
      if (reportId && currentReportIdRef.current === reportId) {
        if (status === 'Completed' || status === 'Resolved') {
          playSound('request-ready');
        } else {
          playSound('notification');
        }
        apiClient.get<MaintenanceReport>(`/api/v1/${hotelSlug}/maintenance/reports/${reportId}`)
          .then(res => setCurrentReport(res.data))
          .catch(err => console.error('Failed to refresh report:', err));
      }
    });
    return unsub;
  }, [on, hotelSlug]);

  useEffect(() => {
    const controller = new AbortController();
    if (hotelSlug) {
      fetchConfig(controller.signal);
    }
    // Use translationStore.language (from localStorage) as source of truth
    if (hotelSlug && language) {
      loadTranslations(hotelSlug, 'maintenance', language);
    }
    return () => controller.abort();
  }, [hotelSlug, language]);

  const fetchConfig = async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      const response = await apiClient.get<ConfigResponse>(`/api/v1/${hotelSlug}/maintenance/config`, { signal });
      setConfig(response.data);

      // Auto-expand first category
      if (response.data.categories.length > 0) {
        setExpandedCategory(response.data.categories[0].id);
      }

      // Load existing reports for reference but don't auto-switch to tracking
      // This allows multiple people in the same room to report different issues
      await loadExistingReports();
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      console.error('Failed to fetch maintenance config:', err);
      setError('Unable to load maintenance services');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingReports = async () => {
    try {
      const response = await apiClient.get<MaintenanceReport[]>(`/api/v1/${hotelSlug}/maintenance/reports`);
      setMyReports(response.data);
      // Don't auto-switch to tracking - let user see the menu
      // They can manually view or track reports if needed
    } catch (err) {
      console.error('Failed to load existing reports:', err);
    }
  };

  // Keep this for manual tracking after submission
  const checkExistingReports = async () => {
    try {
      const response = await apiClient.get<MaintenanceReport[]>(`/api/v1/${hotelSlug}/maintenance/reports`);
      setMyReports(response.data);
      
      // Check for active report to track
      if (response.data.length > 0) {
        const activeReport = response.data.find(r => 
          r.statusCode >= 1 && r.statusCode <= 5 // Reported through OnHold
        );
        if (activeReport) {
          setCurrentReport(activeReport);
          setView('tracking');
          currentReportIdRef.current = activeReport.id;
        }
      }
    } catch (err) {
      console.error('Failed to check existing reports:', err);
    }
  };

  const selectIssueType = (category: MaintenanceCategory, issueType: MaintenanceIssueType) => {
    setSelectedCategory(category);
    setSelectedIssueType(issueType);
    setSubject(issueType.name);
    setView('form');
  };

  const handleSubmitReport = async () => {
    if (!subject.trim()) {
      toast('error', t('maintenance.subjectRequired') || 'Please provide a subject for your report');
      return;
    }
    if (description.trim().length > 0 && description.trim().length < 3) {
      toast('error', t('maintenance.descriptionTooShort') || 'Description must be at least 3 characters');
      return;
    }

    try {
      setSubmitting(true);
      setView('submitting');

      const response = await apiClient.post<{ reportId: string; status: string; message: string }>(
        `/api/v1/${hotelSlug}/maintenance/report`,
        {
          categoryId: selectedCategory?.id,
          issueTypeId: selectedIssueType?.id,
          subject: subject.trim(),
          description: description.trim() || undefined,
          locationDescription: locationDescription.trim() || undefined,
          priority
        }
      );

      // Fetch the created report
      const reportResponse = await apiClient.get<MaintenanceReport>(
        `/api/v1/${hotelSlug}/maintenance/reports/${response.data.reportId}`
      );

      setCurrentReport(reportResponse.data);
      resetForm();
      setView('tracking');
      currentReportIdRef.current = response.data.reportId;
      playSound('request-sent');
      emitActivityChanged();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string | { message?: string } } } };
      const errorMessage = typeof e.response?.data?.error === 'object'
        ? (e.response?.data?.error as { message?: string })?.message
        : (e.response?.data?.error as string)
        || 'Failed to submit report. Please try again.';
      toast('error', errorMessage || 'Failed to submit report. Please try again.');
      setView('form');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedCategory(null);
    setSelectedIssueType(null);
    setSubject('');
    setDescription('');
    setLocationDescription('');
    setPriority(2);
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getPriorityColor = (priorityCode: number) => {
    switch (priorityCode) {
      case 1: return '#6b7280'; // gray
      case 2: return '#3b82f6'; // blue
      case 3: return '#f59e0b'; // amber
      case 4: return '#ef4444'; // red
      default: return '#3b82f6';
    }
  };

  // Loading state
  if (loading) {
    return <PageSpinner />;
  }

  // Error state
  if (error && !config) {
    return (
      <div className="maintenance-page error">
        <div className="error-content">
          <span className="error-icon">⚠️</span>
          <h2>{t('maintenance.oops')}</h2>
          <p>{error}</p>
          <button onClick={() => fetchConfig()} className="retry-btn">{t('common.tryAgain')}</button>
        </div>
      </div>
    );
  }

  // Not enabled
  if (config && (!config.isEnabled || !config.guestCanReport)) {
    return (
      <div className="maintenance-page closed">
        <div className="closed-content">
          <span className="icon">🔧</span>
          <h2>{t('maintenance.unavailable')}</h2>
          <p>{t('maintenance.unavailableMsg')}</p>
          <p>{t('maintenance.contactDesk')}</p>
        </div>
      </div>
    );
  }

  // Submitting state
  if (view === 'submitting') {
    return (
      <div className="maintenance-page placing">
        <div className="placing-content">
          <div className="icon-spin">🔧</div>
          <h2>{t('maintenance.submitting')}</h2>
          <p>{t('maintenance.sendingMsg')}</p>
        </div>
      </div>
    );
  }

  // List view - show all reports
  if (view === 'list') {
    return (
      <div className="maintenance-page list-view">
        <PageHeader
          title={<>📋 {t('maintenance.myReports')}</>}
          leading={
            <button className="back-btn" onClick={() => setView('selection')}>
              ← {t('common.back')}
            </button>
          }
        />

        {myReports.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">📭</span>
            <p>{t('maintenance.noReports')}</p>
          </div>
        ) : (
          <div className="reports-list">
            {myReports.map(report => (
              <button
                key={report.id}
                className="report-card"
                onClick={() => {
                  setCurrentReport(report);
                  setView('tracking');
                  if (report.statusCode < 6) {
                    currentReportIdRef.current = report.id;
                  }
                }}
              >
                <div className="report-header">
                  <span className="category-icon">{report.categoryIcon || '🔧'}</span>
                  <div className="report-info">
                    <span className="report-subject">{report.subject}</span>
                    <span className="report-time">{formatDate(report.reportedAt)}</span>
                  </div>
                  <span 
                    className={`status-badge status-${report.statusCode}`}
                  >
                    {report.status}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        <button
          className="new-report-btn"
          onClick={() => setView('selection')}
        >
          + {t('maintenance.reportNew')}
        </button>
      </div>
    );
  }

  // Tracking view
  if (view === 'tracking' && currentReport) {
    return (
      <div className="maintenance-page tracking">
        <PageHeader
          title={<>🔧 {t('maintenance.reportStatus')}</>}
          leading={
            <button className="back-btn" onClick={() => {
              setCurrentReport(null);
              currentReportIdRef.current = null;
              setView('selection');
            }}>
              ← {t('common.back')}
            </button>
          }
        />

        <div className="report-detail-card">
          <div className="report-summary">
            <div className="summary-header">
              <span className="category-icon">{currentReport.categoryIcon || '🔧'}</span>
              <div className="summary-info">
                <h3>{currentReport.subject}</h3>
                {currentReport.categoryName && (
                  <span className="category-name">{currentReport.categoryName}</span>
                )}
              </div>
              <span 
                className="priority-badge"
                style={{ backgroundColor: getPriorityColor(currentReport.priorityCode) }}
              >
                {currentReport.priority}
              </span>
            </div>
            {currentReport.description && (
              <p className="report-description">{currentReport.description}</p>
            )}
            <p className="report-time">Reported {formatTime(currentReport.reportedAt)}</p>
          </div>

          <div className="progress-tracker">
            {currentReport.progress.map((step, index) => (
              <div 
                key={step.step}
                className={`progress-step ${step.isComplete ? 'complete' : ''} ${step.isCurrent ? 'current' : ''}`}
              >
                <div className="step-indicator">
                  {step.isComplete ? '✓' : step.step}
                </div>
                <div className="step-content">
                  <span className="step-label">{step.label}</span>
                </div>
                {index < currentReport.progress.length - 1 && (
                  <div className={`step-connector ${step.isComplete ? 'complete' : ''}`} />
                )}
              </div>
            ))}
          </div>

          {currentReport.statusCode === 6 && currentReport.resolutionNotes && (
            <div className="resolution-note">
              <span>✅</span>
              <div>
                <strong>{t('maintenance.resolved')}</strong>
                <p>{currentReport.resolutionNotes}</p>
              </div>
            </div>
          )}
        </div>

        <div className="action-buttons">
          <button 
            className="view-all-btn"
            onClick={async () => {
              await checkExistingReports();
              setView('list');
            }}
          >
            {t('maintenance.viewAll')}
          </button>
          <button 
            className="new-report-btn"
            onClick={() => {
              setCurrentReport(null);
              currentReportIdRef.current = null;
              setView('selection');
            }}
          >
            {t('maintenance.reportAnother')}
          </button>
        </div>
      </div>
    );
  }

  // Form view
  if (view === 'form') {
    return (
      <div className="maintenance-page form-view">
        <PageHeader
          title={<>🔧 {t('maintenance.reportIssue')}</>}
          leading={
            <button className="back-btn" onClick={() => setView('selection')}>
              ← {t('common.back')}
            </button>
          }
        />

        {error && (
          <div className="inline-error">
            <span>⚠️</span> {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        <div className="form-card">
          {selectedCategory && selectedIssueType && (
            <div className="selected-type">
              <span className="type-icon">{selectedCategory.icon}</span>
              <div className="type-info">
                <span className="type-category">{selectedCategory.name}</span>
                <span className="type-name">{selectedIssueType.name}</span>
              </div>
              <button 
                className="change-btn"
                onClick={() => setView('selection')}
              >
                {t('maintenance.change')}
              </button>
            </div>
          )}

          <div className="form-group">
            <label>{t('maintenance.subject')} *</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t('maintenance.subjectPlaceholder')}
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label>{t('maintenance.details')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('maintenance.detailsPlaceholder')}
              maxLength={2000}
              rows={4}
            />
          </div>

          <div className="form-group">
            <label>{t('maintenance.location')}</label>
            <input
              type="text"
              value={locationDescription}
              onChange={(e) => setLocationDescription(e.target.value)}
              placeholder={t('maintenance.locationPlaceholder')}
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label>{t('maintenance.priority')}</label>
            <div className="priority-options">
              {[
                { value: 1, label: t('maintenance.priorityLow'), desc: t('maintenance.priorityLowDesc') },
                { value: 2, label: t('maintenance.priorityNormal'), desc: t('maintenance.priorityNormalDesc') },
                { value: 3, label: t('maintenance.priorityHigh'), desc: t('maintenance.priorityHighDesc') },
                { value: 4, label: t('maintenance.priorityUrgent'), desc: t('maintenance.priorityUrgentDesc') }
              ].map(opt => (
                <button
                  key={opt.value}
                  className={`priority-option ${priority === opt.value ? 'selected' : ''}`}
                  onClick={() => setPriority(opt.value)}
                  style={{ 
                    borderColor: priority === opt.value ? getPriorityColor(opt.value) : undefined,
                    backgroundColor: priority === opt.value ? `${getPriorityColor(opt.value)}15` : undefined
                  }}
                >
                  <span className="priority-label">{opt.label}</span>
                  <span className="priority-desc">{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="form-footer">
          <button
            className="submit-btn"
            onClick={handleSubmitReport}
            disabled={!subject.trim() || submitting}
          >
            {t('maintenance.submitReport')}
          </button>
        </div>
      </div>
    );
  }

  // Selection view (default)
  return (
    <div className="maintenance-page selection page-content">
      <PageHeader
        title={<>🔧 {t('maintenance.title')}</>}
        subtitle={
          config?.welcomeMessage ||
          (config?.roomNumber ? `${t('maintenance.room') || 'Room'} ${config.roomNumber}` : undefined)
        }
      />

      {error && (
        <div className="inline-error">
          <span>⚠️</span> {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {myReports.length > 0 && (
        <button 
          className="view-reports-btn"
          onClick={async () => {
            await checkExistingReports();
            setView('list');
          }}
        >
          📋 {t('maintenance.myReports')} ({myReports.length})
        </button>
      )}

      <div className="categories">
        {config?.categories.map(category => (
          <div key={category.id} className="category">
            <button 
              className={`category-header ${expandedCategory === category.id ? 'expanded' : ''}`}
              onClick={() => setExpandedCategory(
                expandedCategory === category.id ? null : category.id
              )}
            >
              <span className="category-icon">{category.icon}</span>
              <span className="category-name">{category.name}</span>
              <span className="expand-icon">{expandedCategory === category.id ? '▼' : '▶'}</span>
            </button>
            
            {expandedCategory === category.id && (
              <div className="category-items">
                {category.issueTypes.map(issueType => (
                  <button
                    key={issueType.id}
                    className="item-card"
                    onClick={() => selectIssueType(category, issueType)}
                  >
                    <span className="item-icon">{issueType.icon || '🔹'}</span>
                    <div className="item-info">
                      <span className="item-name">{issueType.name}</span>
                      {issueType.description && (
                        <span className="item-desc">{issueType.description}</span>
                      )}
                    </div>
                    <span className="select-arrow">→</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="custom-report-section">
        <p>{t('maintenance.customQuestion')}</p>
        <button 
          className="custom-report-btn"
          onClick={() => {
            setSelectedCategory(null);
            setSelectedIssueType(null);
            setSubject('');
            setView('form');
          }}
        >
          {t('maintenance.reportCustom')}
        </button>
      </div>
    </div>
  );
}
